import { CHANNEL_ID, LOG_ID } from '@@types/common';

import { CHANNEL_SETTING_TAB, IncomingHookTabType, MANAGE_HIDDEN_CHANNEL_TAB, Page, PANEL_TYPE } from '~/constants';
import { MINUTE } from '~/constants/date';
import { Place } from '~/contexts/Channel/ChannelContext';
import GA from '~/services/GA/GA';
import globalEventService, { EVENT_NAME } from '~/services/globalEventService';
import { mainDetailUiState, threadDetailUiState } from '~/stores/DetailUiState';
import { debounce } from 'lodash';
import { action, computed, observable } from 'mobx';
import { assoc, equals, findIndex, pick, pipe, remove, values } from 'ramda';

import logging from './providers/logging';

const RELOAD_COUNT = 'RELOAD_COUNT';

type CreateThreadChannelBy = 'editor' | null;

class UiState {
  // 설정
  @observable channelSettingTab = CHANNEL_SETTING_TAB.MEMBER;
  // 우측 panel
  @observable panelType: PANEL_TYPE = PANEL_TYPE.NONE;
  @observable panelSubType = '';
  @observable threadOpen = false;

  @observable
  modalMap = {};

  // popper
  @observable
  popperMap = {};

  // 채널 초대 모달
  @observable inviteSearchKeyword = '';
  // 숨김 채널 관리 모달
  @observable
  manageHiddenChannelTab: MANAGE_HIDDEN_CHANNEL_TAB = MANAGE_HIDDEN_CHANNEL_TAB.Hidden;

  // global
  @observable isOnline = true;
  @observable currentPage = '';
  @observable
  zIndexes = [];

  // modal
  @observable isSessionOut = false;
  @observable isWebsocketOn = false;
  @observable isInterTenantWebsocketOn = false;
  reload = (force = false) => {
    const reloadCount = this.getReloadCount();
    if (force || reloadCount < 5) {
      window.localStorage.setItem(RELOAD_COUNT, String(reloadCount + 1));
      window.location.reload();
    }
  };

  @observable hasFocus = false;
  // Gnb
  @observable.shallow
  gnbListRenderRange = { start: 0, end: 0 };

  @observable gnbTab = '';
  @observable gnbSearchKeyword = '';
  // Gnb Header
  @observable
  isUserMenuOpen = false;

  @observable hoverLog = {};
  @observable hoverLogMenu = {};
  @observable openSelectEmoticon = null;
  @observable hoverTriggerLog: LOG_ID | null = null;

  @observable scrollTargetChannelId: string | null = null;
  // incomming layer
  @observable incomingHookIntegrationTab = IncomingHookTabType.InServices;
  // 채널 관련
  @observable isDeletingChannel = false;
  // side apps
  @observable isFetchingUserApps = true;

  @observable currentContext: 'thread' | 'main' = 'main';

  @observable hasUnreadThread: boolean = false;

  @observable hasUpdatedThreadChannel: boolean = false;

  constructor() {
    globalEventService.addEventListener(EVENT_NAME.ONLINE, () => {
      logging.log('online');
      this.setIsOnline(true);
    });
    globalEventService.addEventListener(EVENT_NAME.OFFLINE, () => {
      logging.log('offline');
      this.setIsOnline(false);
    });

    window.setTimeout(() => {
      window.localStorage.removeItem(RELOAD_COUNT);
    }, 5 * MINUTE);
  }

  @computed
  get isPopupView() {
    return this.currentPage === Page.PopupChannel;
  }

  @computed
  get activePoppers() {
    return values(uiState.popperMap);
  }

  @computed
  get isMainView() {
    return !this.isPopupView;
  }

  get channelIdByContext() {
    return this.currentContext === 'main' ? mainDetailUiState.contentsChannelId : threadDetailUiState.contentsChannelId;
  }

  @action
  setHasUnreadThread = (value: boolean) => {
    this.hasUnreadThread = value;
  };

  @action
  setHasUpdatedThreadChannel = (value: boolean) => {
    this.hasUpdatedThreadChannel = value;
  };

  @action
  setChannelSettingTab = (tab: string) => {
    this.channelSettingTab = tab;
  };

  @action
  setPanelType = (panelType: PANEL_TYPE) => {
    this.panelType = panelType;
  };

  @action
  setPanelSubType = (subType) => {
    this.panelSubType = subType;
  };

  @action
  togglePanelType = (panelType: PANEL_TYPE, subType?: string) => {
    if (this.panelType === panelType) {
      if (subType === this.panelSubType || panelType !== PANEL_TYPE.SIDE_APP) {
        this.panelType = PANEL_TYPE.NONE;
      }
    } else {
      this.panelType = panelType;
    }
    this.setPanelSubType(subType);
  };

  @action
  setThreadOpen = (open: boolean) => {
    this.threadOpen = open;
  };

  @action
  setCurrentContext = (context: 'main' | 'thread') => {
    this.currentContext = context;
  };

  @action
  openModal = ({ name, args = {} }: { name: string; args?: object }) => {
    this.modalMap[name] = { name, args };
  };

  @action
  closeModal = (name: string) => {
    delete this.modalMap[name];
  };

  @action
  closeAllModals = () => {
    this.modalMap = {};
  };

  @action
  openPopper = ({
    name,
    args = {},
    place,
    onClose,
  }: {
    name: string;
    args: object;
    place?: Place;
    onClose?: (name: string) => void;
  }) => {
    this.popperMap[name] = { name, args, place, onClose };
  };

  @action
  closeAllPopper = () => {
    this.popperMap = {};
  };

  @action
  closePopper = (name: string) => {
    delete this.popperMap[name];
  };

  @action
  setInviteSearchKeyword = (inviteSearchKeyword) => {
    this.inviteSearchKeyword = inviteSearchKeyword;
  };

  @action
  setManageHiddenChannelTab = (value: MANAGE_HIDDEN_CHANNEL_TAB) => {
    this.manageHiddenChannelTab = value;
  };

  @action
  setIsOnline = (isOnline) => {
    this.isOnline = isOnline;
  };

  @action
  setCurrentPage = (page: string) => {
    this.currentPage = page;
  };

  @action
  addZIndex = (zIndex) => {
    this.zIndexes.push(zIndex);
  };

  @action
  removeZIndex = (zIndex) => {
    const index = findIndex(equals(zIndex), this.zIndexes);
    this.zIndexes = remove(index, 1, this.zIndexes);
  };

  @action
  clearZIndex = () => {
    this.zIndexes = [];
  };

  @action
  setIsSessionOut = (value: boolean) => {
    this.isSessionOut = value;
  };

  @action
  setIsWebsocketOn = (value: boolean) => {
    if (this.isWebsocketOn !== value) {
      this.isWebsocketOn = value;
    }
  };

  // Detail

  @action
  setIsInterTenantWebsocketOn = (value: boolean) => {
    if (this.isInterTenantWebsocketOn !== value) {
      this.isInterTenantWebsocketOn = value;
    }
  };

  getReloadCount() {
    try {
      return Number(window.localStorage.getItem(RELOAD_COUNT)) || 0;
    } catch (e) {
      return 0;
    }
  }

  @action
  setHasFocus = (hasFocus: boolean) => {
    if (this.hasFocus !== hasFocus) {
      this.hasFocus = hasFocus;
    }
  };

  @action
  setGnbListRenderRange = ({ start, end }) => {
    this.gnbListRenderRange = { start, end };
  };

  @action
  setGnbTab = (tab: string) => {
    this.gnbTab = tab;
  };

  @action
  setGnbSearchKeyword = (keyword) => {
    this.gnbSearchKeyword = keyword;
    this.debounceWriteSearchGa();
  };

  debounceWriteSearchGa = debounce(() => {
    GA.writeGnbMenu(this.gnbTab, 'SEARCH INPUT');
  }, 2000);

  clearGnbSearchKeyword = () => {
    this.setGnbSearchKeyword('');
  };

  @action
  setUserMenuOpen = (value) => {
    this.isUserMenuOpen = value;
  };

  public pickLruChannelIds = (newChannelId: CHANNEL_ID): (<T>(item: Record<string, T>) => Record<string, T>) => {
    return pick([
      mainDetailUiState.currentChannelId,
      mainDetailUiState.prevChannelId,
      threadDetailUiState.currentChannelId,
      threadDetailUiState.prevChannelId,
      newChannelId,
    ]);
  };

  public pickAndSetLruChannelIds = <T>(
    newChannelId: CHANNEL_ID,
    value: T
  ): ((item: Record<string, T>) => Record<string, T>) => {
    return pipe(this.pickLruChannelIds(newChannelId), assoc(newChannelId, value));
  };

  @action
  setHoverLog = (logId: LOG_ID) => {
    if (this.hoverLog[logId]) return;
    this.hoverLog = {
      [logId]: true,
    };
  };

  @action
  clearHoverLog = (logId) => {
    if (this.hoverLog[logId]) {
      this.hoverLog = {};
    }
  };

  @action
  setHoverLogMenu: (logId: string) => void = (logId) => {
    if (this.hoverLogMenu[logId]) return;
    this.hoverLogMenu = { [logId]: true };
  };

  @action
  clearHoverLogMenu: (logId: string) => void = (logId) => {
    if (this.hoverLogMenu[logId]) {
      this.hoverLogMenu = {};
    }
  };

  @action
  setOpenSelectEmoticon = (logId: LOG_ID | null) => {
    this.openSelectEmoticon = logId;
  };

  @action
  setHoverTriggerLog = (logId: LOG_ID | null) => {
    this.hoverTriggerLog = logId;
    if (!logId) {
      this.hoverLogMenu = {};
      this.hoverLog = {};
    }
  };

  @action
  setScrollTargetChannelId = (channelId: string) => {
    this.scrollTargetChannelId = channelId;
  };

  @action
  setScrollTargetChannel = (channel) => {
    this.scrollTargetChannelId = channel.channelId;
  };

  @action
  setIncomingHookIntegrationTab = (tab: IncomingHookTabType) => {
    this.incomingHookIntegrationTab = tab;
  };

  @action
  setIsDeletingChannel = (value) => {
    this.isDeletingChannel = value;
  };

  @action
  setIsFetchingUserApps = (value) => {
    this.isFetchingUserApps = value;
  };

  private currentReconnectTime = 0;

  public setCurrentReconnectTime = (value: number) => {
    this.currentReconnectTime = value;
  };

  public getCurrentReconnectTime = () => {
    return this.currentReconnectTime;
  };

  public createThreadChannelBy: CreateThreadChannelBy = null;

  public setCreateThreadChannelBy = (value: CreateThreadChannelBy) => {
    this.createThreadChannelBy = value;
  };

  public openThreadChannelOnCreateLog: boolean = false;
  private threadChannelTimeoutId = 0;

  public setOpenThreadChannelOnCreateLog = (value: boolean) => {
    this.openThreadChannelOnCreateLog = value;
    window.clearTimeout(this.threadChannelTimeoutId);

    if (value) {
      window.setTimeout(() => {
        this.openThreadChannelOnCreateLog = false;
      }, 1000);
    }
  };
}

export default UiState;

export const uiState = new UiState();
